import {
  customerUrl,
  formatCompanyLabel,
  linkCompanyRows,
  parseJsonLenient,
  type ChartSeries,
  type ChartType,
} from "./chatMessageUtils";
export type TextBlock = {
  type: "text";
  content: string;
};

export type CustomerLinkBlock = {
  type: "customer_link";
  label: string;
  accountNo?: string;
  customerId: string;
  href: string;
};

export type TableBlock = {
  type: "table";
  title?: string;
  columns: string[];
  rows: string[][];
};

export type ListBlock = {
  type: "list";
  title?: string;
  items: string[];
};

export type WarningBlock = {
  type: "warning";
  content: string;
};

export type ChartBlock = {
  type: "chart";
  chartType: ChartType;
  title?: string;
  series: ChartSeries[];
};

export type DoneBlock = {
  type: "done";
};

export type ChatBlock = TextBlock | CustomerLinkBlock | TableBlock | ListBlock | WarningBlock | ChartBlock | DoneBlock;

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const toStringArray = (value: unknown): string[] | null => {
  if (!Array.isArray(value) || !value.every((item) => typeof item === "string")) {
    return null;
  }

  return value;
};

const CHART_TYPES: ChartType[] = ["bar", "line", "pie"];

const normalizeSeries = (value: unknown): ChartSeries | null => {
  if (!isRecord(value) || typeof value.name !== "string" || !Array.isArray(value.data)) {
    return null;
  }
  const data = value.data.map((point) =>
    isRecord(point) && typeof point.label === "string" && typeof point.value === "number" && Number.isFinite(point.value)
      ? { label: point.label, value: point.value }
      : null,
  );
  if (data.length === 0 || data.some((point) => point === null)) {
    return null;
  }
  return { name: value.name, data: data as ChartSeries["data"] };
};

const normalizeChart = (value: Record<string, unknown>): ChartBlock | null => {
  const chartType = CHART_TYPES.find((type) => type === value.chartType);
  if (!chartType || !Array.isArray(value.series) || value.series.length === 0) {
    return null;
  }
  const series = value.series.map(normalizeSeries);
  if (series.some((s) => s === null)) {
    return null;
  }
  return {
    type: "chart",
    chartType,
    title: typeof value.title === "string" ? value.title : undefined,
    series: series as ChartSeries[],
  };
};

const normalizeBlock = (value: unknown): ChatBlock | null => {
  if (typeof value === "string") {
    return { type: "text", content: value };
  }

  if (!isRecord(value) || typeof value.type !== "string") {
    return null;
  }

  switch (value.type) {
    case "text":
      return typeof value.content === "string"
        ? { type: "text", content: value.content }
        : null;
    case "customer_link": {
      if (typeof value.label !== "string" || typeof value.customerId !== "string") {
        return null;
      }
      const href = customerUrl(value.customerId.trim());
      return href
        ? {
            type: "customer_link",
            label: formatCompanyLabel(
              value.label,
              typeof value.accountNo === "string" ? value.accountNo : undefined,
            ),
            accountNo: typeof value.accountNo === "string" ? value.accountNo : undefined,
            customerId: value.customerId.trim(),
            href,
          }
        : { type: "text", content: value.label };
    }
    case "chart":
      return normalizeChart(value);
    case "table": {
      const columns = toStringArray(value.columns);
      const rows = Array.isArray(value.rows)
        ? value.rows.map((row) => toStringArray(row)).filter((row): row is string[] => row !== null)
        : null;

      if (!columns || !rows || rows.some((row) => row.length !== columns.length)) {
        return null;
      }

      const linked = linkCompanyRows(columns, rows);

      return {
        type: "table",
        title: typeof value.title === "string" ? value.title : undefined,
        columns: linked.columns,
        rows: linked.rows,
      };
    }
    case "list": {
      const items = toStringArray(value.items);

      if (!items) {
        return null;
      }

      return {
        type: "list",
        title: typeof value.title === "string" ? value.title : undefined,
        items,
      };
    }
    case "warning":
      return typeof value.content === "string"
        ? { type: "warning", content: value.content }
        : null;
    case "done":
      return { type: "done" };
    default:
      return null;
  }
};

const normalizeBlocks = (value: unknown): ChatBlock[] => {
  if (Array.isArray(value)) {
    return value.map(normalizeBlock).filter((block): block is ChatBlock => block !== null);
  }

  const block = normalizeBlock(value);
  return block ? [block] : [];
};


export const consumeConcatenatedJsonObjects = (
  input: string
): { objects: string[]; remaining: string } => {
  const objects: string[] = [];
  let depth = 0;
  let start = -1;
  let inString = false;
  let escaping = false;
  let lastConsumedEnd = -1;

  for (let i = 0; i < input.length; i++) {
    const ch = input[i];

    if (escaping) {
      escaping = false;
      continue;
    }

    if (ch === "\\") {
      if (inString) {
        escaping = true;
      }
      continue;
    }

    if (ch === '"') {
      inString = !inString;
      continue;
    }

    if (inString) {
      continue;
    }

    if (ch === "{") {
      if (depth === 0) {
        start = i;
      }
      depth++;
      continue;
    }

    if (ch === "}") {
      if (depth === 0) {
        continue;
      }
      depth--;
      if (depth === 0 && start >= 0) {
        objects.push(input.slice(start, i + 1));
        lastConsumedEnd = i;
        start = -1;
      }
    }
  }

  return {
    objects,
    remaining: lastConsumedEnd >= 0 ? input.slice(lastConsumedEnd + 1) : input,
  };
};

export const extractConcatenatedJsonObjects = (input: string): string[] =>
  consumeConcatenatedJsonObjects(input).objects;
const UNDISPLAYABLE_BLOCK_MESSAGE = "Part of this reply couldn't be displayed. Try regenerating the reply.";

const ENVELOPE_PREFIX = '{"blocks":[';
const ENVELOPE_PATTERN = /^\s*\{\s*"blocks"\s*:\s*\[/;
const ENVELOPE_HEAD_LENGTH = 64;

export const resolveReplyEnvelope = (pending: string): { resolved: boolean; text: string } => {
  const compact = pending.slice(0, ENVELOPE_HEAD_LENGTH).replace(/\s+/g, "");

  if (compact.length === 0 || (compact.length < ENVELOPE_PREFIX.length && ENVELOPE_PREFIX.startsWith(compact))) {
    return { resolved: false, text: pending };
  }

  const match = compact.startsWith(ENVELOPE_PREFIX) ? ENVELOPE_PATTERN.exec(pending) : null;
  return { resolved: true, text: match ? pending.slice(match[0].length) : pending };
};

const STRUCTURAL_ONLY_PATTERN = /^[\s{}\[\],:]*$/;

const looksLikeBlockJson = (payload: string): boolean =>
  payload.startsWith("{") && /"type"\s*:\s*"[a-z_]+"/.test(payload);

export const parseSsePayload = (payload: string): ChatBlock[] => {
  const trimmedPayload = payload.trim();

  if (!trimmedPayload || STRUCTURAL_ONLY_PATTERN.test(trimmedPayload)) {
    return [];
  }

  const parseJsonObjectList = (raw: string): ChatBlock[] | null => {
    const chunks = extractConcatenatedJsonObjects(raw);

    if (chunks.length === 0) {
      return null;
    }

    const blocks: ChatBlock[] = [];

    for (const chunk of chunks) {
      let value: unknown;
      try {
        value = parseJsonLenient(chunk);
      } catch {
        if (!looksLikeBlockJson(chunk)) {
          return null;
        }
        blocks.push({ type: "warning", content: UNDISPLAYABLE_BLOCK_MESSAGE });
        continue;
      }

      const block = normalizeBlock(value);
      if (block) {
        blocks.push(block);
      }
    }

    return blocks;
  };

  try {
    const parsed = parseJsonLenient(trimmedPayload);

    if (isRecord(parsed) && Array.isArray(parsed.blocks)) {
      return normalizeBlocks(parsed.blocks);
    }

    if (isRecord(parsed) && "data" in parsed) {
      if (typeof parsed.data === "string") {
        const fromString = parseJsonObjectList(parsed.data);

        if (fromString && fromString.length > 0) {
          return fromString;
        }
      }

      return normalizeBlocks(parsed.data);
    }

    return normalizeBlocks(parsed);
  } catch {
    const directBlocks = parseJsonObjectList(trimmedPayload);

    if (directBlocks) {
      return directBlocks;
    }

    if (looksLikeBlockJson(trimmedPayload)) {
      return [{ type: "warning", content: UNDISPLAYABLE_BLOCK_MESSAGE }];
    }

    return [{ type: "text", content: payload }];
  }
};


export const appendBlocks = (currentBlocks: ChatBlock[], incomingBlocks: ChatBlock[]): ChatBlock[] => {
  const nextBlocks = [...currentBlocks];

  for (const block of incomingBlocks) {
    if (block.type === "done") {
      continue;
    }

    const lastIndex = nextBlocks.length - 1;
    const previousBlock = nextBlocks[lastIndex];

    if (block.type === "text" && previousBlock?.type === "text") {
      nextBlocks[lastIndex] = { ...previousBlock, content: previousBlock.content + block.content };
      continue;
    }

    nextBlocks.push(block);
  }

  return nextBlocks;
};
